import { useEffect, useRef } from 'react';

import { useBrowsing } from 'hooks/use-browsing';

import styles from './header-navigation.module.scss';

export type TopBannerSlotId =
	| 'homepage_above_nav'
	| 'ticketing_above_nav'
	| 'article_above_nav'
	| 'players_above_nav';

type TopBannerProps = {
	slotId?: TopBannerSlotId | null;
};

const getSlotId = (pathname: string): TopBannerSlotId | null => {
	const path = pathname.toLowerCase().replace(/\/$/, '') || '/';

	if (path === '/') return 'homepage_above_nav';
	if (path === '/tickets' || path.startsWith('/tickets/'))
		return 'ticketing_above_nav';
	if (path === '/players' || path.startsWith('/players/'))
		return 'players_above_nav';
	if (path.startsWith('/news/') || path.startsWith('/citytv/'))
		return 'article_above_nav';

	return null;
};

export const TopBanner = ({ slotId }: TopBannerProps) => {
	const { pathname, asPath, basePath } = useBrowsing();
	const path = `${basePath || ''}${asPath || pathname}`.split(/[?#]/)[0];
	const resolvedSlotId = slotId === undefined ? getSlotId(path) : slotId;
	const bannerRef = useRef<HTMLElement>(null);
	const displayedSlots = useRef(new Set<TopBannerSlotId>());
	const slotIdsRef = useRef<TopBannerSlotId[]>([]);
	const slotIds =
		resolvedSlotId && !slotIdsRef.current.includes(resolvedSlotId)
			? [...slotIdsRef.current, resolvedSlotId]
			: slotIdsRef.current;
	slotIdsRef.current = slotIds;

	useEffect(() => {
		const banner = bannerRef.current;
		if (!banner || !resolvedSlotId) return;
		const slot = document.getElementById(resolvedSlotId);

		let isVisible = false;
		let disposed = false;
		let content: HTMLIFrameElement | null = null;
		let scrollTimeout: ReturnType<typeof setTimeout> | undefined;

		const update = () => {
			const iframe = slot?.querySelector('iframe') ?? null;
			if (iframe !== content) {
				if (content) resizeObserver?.unobserve(content);
				content = iframe;
				if (content) resizeObserver?.observe(content);
			}

			if (!content) {
				isVisible = false;
				if (banner.style.height !== '0px') banner.style.height = '0px';
				return;
			}

			const contentStyle = window.getComputedStyle(content);
			const height =
				content.getBoundingClientRect().height +
				(parseFloat(contentStyle.marginTop) || 0) +
				(parseFloat(contentStyle.marginBottom) || 0);

			if (isVisible && window.scrollY > 410 + height) {
				isVisible = false;
			} else if (
				!isVisible &&
				window.scrollY <= 410 &&
				window.getComputedStyle(document.body).position !== 'fixed'
			) {
				isVisible = true;
			}

			const bannerHeight = isVisible ? `${height}px` : '0px';
			if (banner.style.height !== bannerHeight) {
				banner.style.height = bannerHeight;
			}
		};

		const resizeObserver =
			typeof ResizeObserver === 'undefined'
				? null
				: new ResizeObserver(update);
		const mutationObserver = new MutationObserver(update);
		mutationObserver.observe(banner, {
			childList: true,
			subtree: true,
			attributes: true,
		});

		const handleScroll = () => {
			if (scrollTimeout !== undefined) return;
			scrollTimeout = setTimeout(() => {
				scrollTimeout = undefined;
				update();
			}, 300);
		};

		window.addEventListener('scroll', handleScroll, { passive: true });
		window.addEventListener('resize', update);
		banner.addEventListener('load', update, true);
		update();

		const adWindow = window as Window & {
			googletag?: {
				cmd: { push: (callback: () => void) => unknown };
				display?: (id: string) => void;
			};
		};
		const googletag: NonNullable<typeof adWindow.googletag> =
			adWindow.googletag ?? { cmd: new Array<() => void>() };
		adWindow.googletag = googletag;
		googletag.cmd.push(() => {
			if (
				!disposed &&
				!displayedSlots.current.has(resolvedSlotId) &&
				googletag.display
			) {
				googletag.display(resolvedSlotId);
				displayedSlots.current.add(resolvedSlotId);
			}
		});

		return () => {
			disposed = true;
			mutationObserver.disconnect();
			resizeObserver?.disconnect();
			window.removeEventListener('scroll', handleScroll);
			window.removeEventListener('resize', update);
			banner.removeEventListener('load', update, true);
			clearTimeout(scrollTimeout);
		};
	}, [resolvedSlotId]);

	if (!slotIds.length) return null;

	return (
		<aside
			ref={bannerRef}
			className={styles.topBanner}
			aria-label="Top banner"
			hidden={!resolvedSlotId}
		>
			{slotIds.map((id) => (
				<div key={id} id={id} hidden={id !== resolvedSlotId} />
			))}
		</aside>
	);
};
